import React, { useCallback, useEffect, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";
import {
  AlertTriangle, ShieldOff, Settings, X, Loader2, ChevronDown, ChevronUp,
} from "lucide-react";
import { useAuth } from "@/contexts/AuthContext";
import { usePinSecurity } from "@/contexts/PinContext";

// V1.37.2 · Banner de salud reCAPTCHA
// Reglas:
//  - Solo se muestra a super_admin (el endpoint /health devuelve
//    {unresolved:false} para el resto de usuarios).
//  - Polling ligero cada 90s.
//  - Desaparece automáticamente si `unresolved=false` (resuelto o descartado).
//  - Acciones rápidas: Ver detalles / Ir a Seguridad / Desactivar reCAPTCHA.

const POLL_MS = 90_000;

const HUMAN_ERROR = {
  google_unreachable: "No se pudo contactar con Google (red/DNS)",
  google_timeout: "Timeout al validar con Google (>4 s)",
  "server_misconfig:invalid-input-secret": "Clave Secret inválida en Google",
  "server_misconfig:missing-input-secret": "Falta la clave Secret",
  "server_misconfig:bad-request": "Solicitud rechazada por Google",
  missing_token_but_enabled: "El navegador del usuario no envió token",
  secret_missing: "Falta configurar la Secret Key",
};

function humanize(err) {
  if (!err) return "";
  // limpia prefijos verification_soft_fail:xxx
  const clean = err.replace(/^verification_soft_fail:/, "").replace(/^server_misconfig:/, "");
  return HUMAN_ERROR[err]
      || HUMAN_ERROR[`server_misconfig:${clean}`]
      || clean.replace(/[-_]/g, " ");
}

export default function RecaptchaHealthBanner({ onNavigate }) {
  const { user } = useAuth();
  const pin = usePinSecurity();
  const [health, setHealth] = useState(null);
  const [expanded, setExpanded] = useState(false);
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    if (!user?.is_super_admin) return;
    try {
      const { data } = await api.get("/security/recaptcha/health");
      setHealth(data);
    } catch {
      // silencioso — nunca romper el dashboard por un fallo del banner
    }
  }, [user?.is_super_admin]);

  useEffect(() => {
    load();
    const t = setInterval(load, POLL_MS);
    return () => clearInterval(t);
  }, [load]);

  if (!user?.is_super_admin) return null;
  if (!health?.unresolved) return null;

  const dismiss = async () => {
    setBusy(true);
    try {
      await api.post("/security/recaptcha/health/dismiss");
      toast.success("Aviso descartado");
      setHealth((h) => ({ ...h, unresolved: false }));
    } catch (err) {
      toast.error(formatApiError(err?.response?.data?.detail) || "Error");
    } finally {
      setBusy(false);
    }
  };

  const quickDisable = async () => {
    setBusy(true);
    try {
      await pin.require("setting.update");
      await api.post("/security/recaptcha/disable");
      toast.success("reCAPTCHA desactivado");
      setHealth((h) => ({ ...h, unresolved: false, enabled: false }));
    } catch (err) {
      const m = formatApiError(err?.response?.data?.detail) || err?.message;
      if (m && !/cancel/i.test(m)) toast.error(m);
    } finally {
      setBusy(false);
    }
  };

  const gotoSecurity = () => {
    onNavigate?.("config");
    // Delegamos scroll a la sección tras un tick para que el tab renderice
    setTimeout(() => {
      const el = document.getElementById("settings-recaptcha");
      if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
    }, 400);
  };

  const mins = health.minutes_since_error || 0;
  const timeLabel =
    mins < 1 ? "hace menos de 1 minuto" :
    mins < 60 ? `hace ${mins} minuto${mins === 1 ? "" : "s"}` :
    mins < 1440 ? `hace ${Math.floor(mins/60)} h ${mins%60} min` :
    `hace ${Math.floor(mins/1440)} día${Math.floor(mins/1440)===1?"":"s"}`;

  return (
    <div
      className="mb-4 rounded-2xl border border-amber-300 bg-gradient-to-r from-amber-50 to-orange-50 p-3 sm:p-4 shadow-sm"
      data-testid="recaptcha-health-banner"
    >
      <div className="flex items-start gap-3">
        <div className="rounded-lg bg-amber-500 text-white p-2 shrink-0">
          <AlertTriangle className="h-4 w-4" />
        </div>
        <div className="flex-1 min-w-0">
          <div className="font-bold text-amber-900 leading-tight">
            Error de reCAPTCHA detectado {timeLabel}
          </div>
          <div className="text-xs text-amber-800 mt-0.5">
            <b>Motivo:</b> {humanize(health.last_error) || health.last_error}
            {" · "}
            <button
              type="button"
              onClick={() => setExpanded((v) => !v)}
              className="underline decoration-dotted hover:text-amber-900"
              data-testid="recaptcha-banner-details-toggle"
            >
              {expanded ? "Ocultar detalles" : "Ver detalles"}
              {expanded ? <ChevronUp className="inline h-3 w-3 ml-0.5" /> : <ChevronDown className="inline h-3 w-3 ml-0.5" />}
            </button>
          </div>

          {expanded && (
            <div className="mt-2 rounded-lg bg-white/70 border border-amber-200 p-2 text-[11px] text-amber-900"
                 data-testid="recaptcha-banner-details">
              <div className="tabular-nums">
                <b>Último error:</b> {health.last_error_at || "—"}<br />
                <b>Último éxito:</b> {health.last_success_at || "nunca"}<br />
                <b>Estado enabled:</b> {String(health.enabled)}
              </div>
              {health.error_log && health.error_log.length > 0 && (
                <div className="mt-2">
                  <div className="font-bold uppercase tracking-widest text-[9px]">
                    Últimos {health.error_log.length} errores
                  </div>
                  <ul className="list-disc list-inside mt-0.5 space-y-0.5">
                    {[...health.error_log].reverse().slice(0, 6).map((e, i) => (
                      <li key={i} className="truncate">
                        <span className="tabular-nums opacity-70">{e.at?.slice(11, 19) || ""}</span>
                        {" · "}
                        {humanize(e.error) || e.error}
                      </li>
                    ))}
                  </ul>
                </div>
              )}
            </div>
          )}

          <div className="mt-2 flex flex-wrap items-center gap-1.5">
            <button
              type="button"
              onClick={gotoSecurity}
              disabled={busy}
              className="inline-flex items-center gap-1 rounded-full bg-white border border-amber-300 hover:border-amber-500 hover:bg-amber-50 text-amber-900 text-[11px] font-semibold px-3 py-1"
              data-testid="recaptcha-banner-go-security"
            >
              <Settings className="h-3 w-3" /> Ir a Seguridad
            </button>
            <button
              type="button"
              onClick={quickDisable}
              disabled={busy || !health.enabled}
              className="inline-flex items-center gap-1 rounded-full bg-red-600 hover:bg-red-700 text-white text-[11px] font-semibold px-3 py-1 disabled:opacity-40"
              data-testid="recaptcha-banner-disable"
              title={health.enabled ? "Desactivar reCAPTCHA rápidamente (requiere PIN)" : "Ya está desactivado"}
            >
              {busy ? <Loader2 className="h-3 w-3 animate-spin" /> : <ShieldOff className="h-3 w-3" />}
              Desactivar reCAPTCHA
            </button>
            <button
              type="button"
              onClick={dismiss}
              disabled={busy}
              className="inline-flex items-center gap-1 rounded-full text-amber-800 hover:bg-amber-100 text-[11px] font-semibold px-2 py-1"
              data-testid="recaptcha-banner-dismiss"
            >
              <X className="h-3 w-3" /> Descartar
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
